package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.localeInfo
import dev.lingvo.shared.screenshots.ScreenshotLooks
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.SceneKind
import dev.lingvo.shared.screenshots.doc.SceneSpec
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.scene.SceneInput
import dev.lingvo.site.components.studio.preview.DesignLooksPort
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.app.projects.slug.screenshots.DesignAutosaveController
import dev.lingvo.site.pages.app.projects.slug.screenshots.DesignPageData
import dev.lingvo.site.pages.app.projects.slug.screenshots.ScreenshotsAreaState
import dev.lingvo.site.pages.app.projects.slug.screenshots.ScreenshotsCell
import dev.lingvo.site.pages.app.projects.slug.screenshots.ScreenshotsTab
import dev.lingvo.site.pages.app.projects.slug.screenshots.captionValue
import dev.lingvo.site.pages.app.projects.slug.screenshots.screenshotsAgentsPath
import dev.lingvo.site.pages.app.projects.slug.screenshots.screenshotsPath
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.api.Session
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.t

/** Route adapter only: the existing editor owns gestures/history and W2-10 owns persistence. */
@Composable
internal fun CanvasDesignWorkspace(
    data: DesignPageData, autosave: DesignAutosaveController, params: ScreenshotsAreaState,
    looks: DesignLooksPort, canWrite: Boolean, narrow: Boolean,
    onParams: (ScreenshotsAreaState) -> Unit, navigate: (String) -> Unit,
    onPendingCaption: (Boolean) -> Unit, onCaptionSaved: () -> Unit = {}, brand: @Composable (ScreenshotDesign) -> Unit,
) {
    val doc = autosave.document ?: return
    val locales = (listOf(data.project.sourceLocale) + data.project.locales).distinct()
    val location = canvasLocation(data.plan, doc, params.slot, params.device?.id, params.locale, params.layer, locales)
        ?: return
    val scope = rememberCoroutineScope()
    val toasts = rememberToasts()
    val ports = remember(data.project.id, data.plan.platform, looks) { CanvasDesignPorts(data, looks) }
    val queue = remember(ports) { dev.lingvo.site.components.studio.preview.PreviewQueue() }
    var inputRevision by remember(ports) { mutableStateOf(0) }
    var fontRetry by remember(ports) { mutableStateOf(0) }
    val latestDoc by rememberUpdatedState(doc)
    val latestParams by rememberUpdatedState(params)
    val latestWrite by rememberUpdatedState(canWrite)
    val refreshCaptions by rememberUpdatedState(onCaptionSaved)
    val latestNavigate by rememberUpdatedState(navigate)
    var generation by remember { mutableStateOf(0) }
    // External replacements (Load theirs / Restore / template) retire the old history. Acknowledging
    // the current document does not reset its undo stack or remount the stage.
    var previous by remember { mutableStateOf<StageController?>(null) }
    LaunchedEffect(doc) {
        if (previous?.history?.document?.let { it != doc } == true) generation++
    }
    key(generation) {
        val stage = remember(ports, generation) {
            StageController(
                StudioUiState(doc, location.view, location.view.selection, saveLabel = autosave.statusLabel,
                    readOnlyReason = if (canWrite) null else readOnlyCanvasReason(),
                    slots = doc.boards.first { it.deviceClass == location.view.device }.pages.flatMap { it.slotIds }.map { slot ->
                        StudioSlot(slot, data.plan.slots.firstOrNull { it.key == slot }?.title ?: slot, "")
                    }, languages = locales.map { locale -> StudioLanguage(locale, localeInfo(locale).englishName, "",
                        captionValue(data, locale, location.view.slotId).orEmpty().length) },
                    scenes = SceneKind.entries.map(::SceneSpec)),
                renderInputs = { view, measurer -> ports.inputs(latestDoc, view, measurer) },
                onDocumentCommit = { if (latestWrite) autosave.edit(it) },
                onExternalIntent = external@ { intent ->
                    if (intent == StudioIntent.RetryFonts) { fontRetry++; return@external }
                    val current = previous
                    if (current != null && !captionPending(current.uiState.caption)) {
                        val state = latestParams
                        val destination = when (intent) {
                            StudioIntent.Plan -> screenshotsPath(data.project.slug, ScreenshotsTab.PLAN, state)
                            StudioIntent.Review -> screenshotsPath(data.project.slug, ScreenshotsTab.REVIEW, state)
                            StudioIntent.UploadCapture -> screenshotsPath(data.project.slug, ScreenshotsTab.PLAN,
                                state.copy(cell = ScreenshotsCell(data.plan.slots.first { it.key == current.uiState.view.slotId }.id, current.uiState.view.locale)))
                            StudioIntent.AskAgent -> screenshotsAgentsPath(data.project.slug, state)
                            else -> null
                        }
                        if (destination != null && (autosave.state == dev.lingvo.site.pages.app.projects.slug.screenshots.DesignSaveState.CLEAN ||
                                window.confirm(tr("studio.editor.canvas_design_workspace.leave_the_design_studio_unsaved_changes", "Leave the design studio? Unsaved changes are still waiting to be saved.")))) {
                            autosave.flush()
                            latestNavigate(destination)
                        }
                    } else if (current != null) current.updateState { it.copy(error = pendingCaptionMessage()) }
                }, scope = scope, toasts = toasts, captureCache = ports.captures,
                scenes = ScreenshotLooks.scenes, sceneInput = { state, scene ->
                    val frame = allLayers(state.layers).filterIsInstance<DeviceLayer>().firstOrNull()?.frame
                        ?: looks.defaultFrame(DeviceClass.fromId(state.view.device)!!)
                    SceneInput(state.board.canvas, state.page.slotIds.size, ScreenshotLooks.safeArea(state.board, frame), scene,
                        frame, ports.capture(state.view, state.view.slotId)?.let { it.height.toDouble() / it.width }
                            ?: (state.board.canvas.height.toDouble() / state.board.canvas.width))
                }, sceneCommand = { state, scene ->
                    val frame = allLayers(state.layers).filterIsInstance<DeviceLayer>().firstOrNull()?.frame
                        ?: looks.defaultFrame(DeviceClass.fromId(state.view.device)!!)
                    canvasSceneCommand(state, scene, ScreenshotLooks.scenes, SceneInput(state.board.canvas,
                        state.page.slotIds.size, ScreenshotLooks.safeArea(state.board, frame), scene, frame,
                        state.board.canvas.height.toDouble() / state.board.canvas.width))
                },
            )
        }
        SideEffect {
            previous = stage
            ports.data = data
            ports.writable = canWrite
            ports.activeCaption = stage.uiState.caption
            ports.changed = { stage.inputsChanged(); inputRevision++; refreshCaptions() }
            val reason = if (canWrite) null else readOnlyCanvasReason()
            if (stage.uiState.saveLabel != autosave.statusLabel || stage.uiState.readOnlyReason != reason)
                stage.updateState { it.copy(saveLabel = autosave.statusLabel, readOnlyReason = reason) }
            val artwork = data.artwork.map { StudioAsset(it.id, it.id, "", ImageSource.Asset(it.id)) }
            if (stage.uiState.assets != artwork) stage.updateState { it.copy(assets = artwork) }
            onPendingCaption(captionPending(stage.uiState.caption))
        }
        val state = stage.uiState
        LaunchedEffect(stage, state.document, state.view.locale, state.view.device, state.caption?.draft, data.captions, fontRetry) {
            val language = localeInfo(state.view.locale).englishName
            stage.updateState { it.copy(fontStatus = trf("studio.font.loading", "Loading {language} font…", "language" to language), fontFailed = false) }
            stage.inputsChanged()
            try {
                ports.prepareFonts(state.document, state.view)
                currentCoroutineContext().ensureActive()
                stage.updateState { it.copy(fontStatus = null, fontFailed = false) }
                stage.inputsChanged()
                stage.captionBinding?.let { binding ->
                    val caption = binding.snapshot
                    binding.refreshFont(ports.fontFor(state.document, state.view, caption.layer, caption.draft))
                }
                inputRevision++
            } catch (cancelled: CancellationException) { throw cancelled }
            catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                stage.updateState { it.copy(fontStatus = trf("studio.font.failed", "The {language} font couldn't load. Showing a fallback font.", "language" to language), fontFailed = true) }
                stage.inputsChanged()
            }
        }
        val sync = remember(stage) { CanvasUrlSync() }
        LaunchedEffect(location, state.view, state.selection) {
            if (sync.incoming != location) {
                sync.incoming = location
                stage.openLocation(location.view)
            }
            val view = stage.uiState.view
            val slotId = data.plan.slots.firstOrNull { it.key == view.slotId }?.id
            val next = params.copy(slot = slotId, device = DeviceClass.fromId(view.device), locale = view.locale,
                layer = stage.uiState.selection.singleOrNull())
            if (next != params) {
                sync.incoming = canvasLocation(data.plan, latestDoc, next.slot, next.device?.id, next.locale, next.layer, locales)
                if (next.slot != params.slot || next.device != params.device || next.locale != params.locale) autosave.flush()
                onParams(next)
            }
        }
        LaunchedEffect(stage, state.document, state.view.locale, state.view.device, state.view.slotId, inputRevision, data.captions) {
            try {
                ports.prepare(state.document, state.view)
                stage.updateState { it.copy(captureAvailable = ports.capture(it.view, it.view.slotId) != null) }
                stage.inputsChanged()
                delay(300)
                val board = state.document.boards.first { it.deviceClass == state.view.device }
                for (slot in board.pages.flatMap { it.slotIds }) {
                    val thumbnail = kotlinx.coroutines.suspendCancellableCoroutine<dev.lingvo.site.components.studio.preview.Thumbnail> { pending ->
                        queue.enqueue(slot) {
                            if (pending.isActive) try { pending.resumeWith(Result.success(ports.thumbnail(state.document, state.view, slot))) }
                            catch (failure: Exception) { pending.resumeWith(Result.failure(failure)) }
                        }
                        pending.invokeOnCancellation { queue.cancel(slot) }
                    }
                    stage.updateState { current -> current.copy(slots = current.slots.map {
                        if (it.id != slot) it else it.copy(thumbnailUrl = thumbnail.dataUrl,
                            status = if (!ports.fontsReady(state.document, state.view)) tr("studio.editor.canvas_design_workspace.fallback_font", "Fallback font")
                                else if (thumbnail.report.blocking) tr("studio.editor.canvas_design_workspace.needs_attention", "Needs attention")
                                else tr("studio.editor.canvas_design_workspace.ready", "Ready"))
                    }) }
                }
            } catch (cancelled: CancellationException) { throw cancelled }
            catch (_: Exception) { stage.updateState { it.copy(error = tr("studio.editor.canvas_design_workspace.images_failed", "Some canvas images couldn't load. Reopen Canvas to retry.")) } }
        }
        val textLayer = state.selected.filterIsInstance<TextLayer>().singleOrNull()
        LaunchedEffect(stage, textLayer?.base?.id, textLayer?.content, state.view.locale, state.view.slotId, canWrite, fontRetry) {
            if (captionPending(stage.uiState.caption)) return@LaunchedEffect
            stage.clearCaption()
            val layer = textLayer ?: return@LaunchedEffect
            val slotKey = when (val content = layer.content) {
                is TextContent.SlotCaption -> state.page.slotIds.getOrNull(content.slot)
                is TextContent.Key -> content.key
                is TextContent.Literal -> null
            } ?: return@LaunchedEffect
            val keyId = data.plan.slots.firstOrNull { it.key == slotKey }?.captionKeyId
                ?: data.captions.values.flatten().firstOrNull { it.key.name == slotKey }?.key?.id ?: return@LaunchedEffect
            try {
                val text = captionValue(data, state.view.locale, slotKey).orEmpty()
                val font = ports.fontFor(latestDoc, state.view, layer, text)
                val caption = ports.read { loadCaptionSnapshot(data.project.slug, keyId, state.view.locale,
                    localeInfo(state.view.locale).englishName, layer, font, canWrite,
                    canWrite && ProjectAuthority.resolve(data.project, Session.organizations).canEditStructure) }
                val canvas = document.createElement("canvas") as org.w3c.dom.HTMLCanvasElement
                val measurer = looks.measurerFor(canvas)
                val fitter = CaptionFitPort { value, locale, currentLayer ->
                    EngineCaptionFitPort(ports.inputs(latestDoc, stage.uiState.view, measurer), latestDoc.brand).fit(value, locale, currentLayer)
                }
                val actualFont = ports.fontFor(latestDoc, state.view, layer, caption.value.ifEmpty { caption.sourceText.orEmpty() })
                stage.setCaption(caption.copy(font = actualFont), fitter, ports.keys)
            } catch (cancelled: CancellationException) { throw cancelled }
            catch (_: Exception) { stage.updateState { it.copy(error = tr("studio.editor.canvas_design_workspace.the_caption_couldnt_load_select_it", "The caption couldn't load. Select it again to retry.")) } }
        }
        DisposableEffect(stage) {
            val guard = EventListener { event ->
                if (captionPending(stage.uiState.caption)) {
                    val anchor = (event.target as? HTMLElement)?.closest("a[href]")
                    if (anchor != null) { event.preventDefault(); event.stopImmediatePropagation(); stage.updateState { it.copy(error = pendingCaptionMessage()) } }
                }
            }
            val unload = EventListener { event -> if (captionPending(stage.uiState.caption)) { event.preventDefault(); event.asDynamic().returnValue = "" } }
            document.addEventListener("click", guard, true)
            window.addEventListener("beforeunload", unload)
            onDispose {
                document.removeEventListener("click", guard, true); window.removeEventListener("beforeunload", unload)
                stage.detach(); onPendingCaption(false)
            }
        }
        // Keep the controller alive on resize; hide the desktop surface and retain caption drafts.
        Div(attrs = { if (narrow) style { property("display", "none") }
            else style { property("height", "min(900px, 85dvh)"); property("min-height", "720px"); property("width", "100%"); property("min-width", "0"); property("flex-shrink", "0") } }) {
            StudioScreen(stage) { brand(autosave.document ?: doc) }
        }
        ToastHost(toasts)
    }
    DisposableEffect(ports) { onDispose { queue.close(); ports.close() } }
}

private class CanvasUrlSync { var incoming: CanvasLocation? = null }
private fun readOnlyCanvasReason() = tr("studio.editor.canvas_design_workspace.this_design_is_read_only_editing", "This design is read-only. Editing is paused for your current role or while a push or restore is running.")
private fun pendingCaptionMessage() = tr("studio.editor.canvas_design_workspace.save_or_cancel_this_caption_before", "Save or cancel this caption before leaving Canvas or changing the selection.")

/** External URL navigation uses the same history and dirty-caption rule as the editor's own controls. */
internal fun StageController.openLocation(view: HistoryView): Boolean {
    if (view == history.view.copy(selection = selection.ids)) return true
    if (captionPending(uiState.caption)) { updateState { it.copy(error = pendingCaptionMessage()) }; return false }
    val board = history.document.boards.firstOrNull { it.deviceClass == view.device } ?: return false
    if (board.pages.none { view.slotId in it.slotIds }) return false
    clearCaption()
    history.switchView(view); selection.restore(view.selection)
    viewport = viewport.copy(documentWidth = board.canvas.width.toDouble(), documentHeight = board.canvas.height.toDouble()).fit()
    updateState { it.copy(error = null, slots = board.pages.flatMap { it.slotIds }.map { key ->
        it.slots.firstOrNull { slot -> slot.id == key } ?: StudioSlot(key, key, "")
    }) }
    publish()
    return true
}

private fun StageController.clearCaption() {
    captionBinding?.dispose(); captionBinding = null; inlineEditing = false
    updateState { it.copy(caption = null) }
}
